import React, { useEffect, useMemo, useRef, useState, Suspense } from 'react';
import Label from './Label/Label';
import { countries as allCountries } from '../../../lib/countries';
import useCheckoutState from '../../../hooks/useCheckoutState';
import ErrorMessage from './ErrorMessage';
import TextInput from '../../Form/components/TextInput/TextInput';
import { isMobile } from '../../../lib/is-mobile';
import { dynamicImportAgnostic } from '../../../lib/dynamicImportAgnostic';

// Use the utility function to import PhoneInput
const PhoneInput = dynamicImportAgnostic(() => import('./PhoneInput'), { ssr: false });

const errorMap = [
  'Invalid number',
  'Invalid country code',
  'Too short',
  'Too long',
  'Invalid number',
];

const parsePhoneFieldValue = value => {
  if (typeof value != 'string') return;
  return value.replaceAll('+', ' ');
};

export default function PhoneField({ field, onChange }) {
  const { countryCode } = useCheckoutState();
  const [isValid, setIsValid] = useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const { label, limitAllowedCountries } = field;
  const [isTouched, setIsTouched] = useState(false);
  const phoneFieldWrapperRef = useRef();
  const phoneFieldRef = useRef();
  const [itiInstance, setItiInstance] = useState(null);
  const [initialValue, setInitialValue] = useState(
    parsePhoneFieldValue(
      field.value
        ? field.value
        : field.defaultValue?.enabled
          ? field.defaultValue?.value
          : undefined,
    ),
  );
  const [isPhoneInputLoaded, setIsPhoneInputLoaded] = useState(false);

  let countries;
  if (limitAllowedCountries?.enabled && limitAllowedCountries.countries?.length > 0) {
    countries = allCountries.filter(country =>
      limitAllowedCountries.countries?.includes(country.alpha2),
    );
  } else {
    countries = allCountries;
  }

  useEffect(() => {
    setInitialValue(
      parsePhoneFieldValue(
        field.value
          ? field.value
          : field.defaultValue?.enabled
            ? field.defaultValue?.value
            : undefined,
      ),
    );
  }, [field.defaultValue]);

  function handleChange(value) {
    // Prevent setting the same value to prevent infinite loop
    if (field.value === value) return;

    setIsValid(value.isValid);
    setErrorCode(value.errorCode);
    onChange(value);
  }

  // Set the initial country based on the country code
  // Only if there's no initial phone number value
  const initialCountry = useMemo(() => {
    if (field.value) return ''; // Let the phone number determine the country

    return (
      countries.find(country => country.alpha2 === countryCode)?.alpha2 || countries[0].alpha2 || ''
    );
  }, [countries, countryCode, field.value]);

  const isSingleCountry = countries.length === 1;
  const lessThanFiveCountries = countries.length < 5;

  const initOptions = {
    initialCountry,
    formatOnDisplay: true,
    onlyCountries: countries.map(country => country.alpha2),
    showFlags: false,
    ...(isSingleCountry && {
      separateDialCode: field.showSelectedDialCode,
      allowDropdown: false,
      nationalMode: true,
    }),
    ...(!isSingleCountry && {
      separateDialCode: true,
      ...(lessThanFiveCountries && {
        countrySearch: false,
      }),
    }),
    useFullscreenPopup: isMobile(),
  };

  const selectedCountry = itiInstance?.getSelectedCountryData();

  // Prevent unsetting the country with keyboard if already selected
  useEffect(() => {
    if (!phoneFieldRef.current) return;
    if (!itiInstance) return;

    // Only set country if there's no phone number value
    if (!selectedCountry && !initialValue && initialCountry) {
      itiInstance.setCountry(initialCountry);
    }
  }, [initialCountry, phoneFieldRef, field.value, isPhoneInputLoaded]);

  useEffect(() => {
    if (!phoneFieldRef.current) return;
    const iti = phoneFieldRef.current.getInstance();
    setItiInstance(iti);
  }, [isPhoneInputLoaded, phoneFieldRef.current]);

  // reset isTouched when field value is empty
  useEffect(() => {
    if (field.value === '') {
      setIsTouched(false);
    }
  }, [field.value]);

  useEffect(() => {
    if (!phoneFieldWrapperRef.current) return;
    if (isSingleCountry) return;
    if (!isPhoneInputLoaded) return; // Only run after PhoneInput has loaded

    const input = phoneFieldWrapperRef.current.querySelector('input[type="tel"]');

    if (!input) return;

    function increasePaddingLeft() {
      const currentPaddingLeft = Number(
        input.style.getPropertyValue('padding-left').replace('px', ''),
      );

      const newPaddingLeft = currentPaddingLeft + 6;

      input.style.setProperty('padding-left', `${newPaddingLeft}px`);
    }

    input.addEventListener('countrychange', increasePaddingLeft);

    increasePaddingLeft();

    return () => {
      input.removeEventListener('countrychange', increasePaddingLeft);
    };
  }, [phoneFieldWrapperRef.current, isPhoneInputLoaded]);

  return (
    <div ref={phoneFieldWrapperRef}>
      {label && (
        <Label htmlFor={field._id} className="cp_form-label" required={field.required}>
          {label}
        </Label>
      )}

      <Suspense fallback={<TextInput className="cp_form-phone w-full invisible" />}>
        <PhoneInput
          key={JSON.stringify([
            initOptions.onlyCountries,
            initOptions.showSelectedDialCode,
            initOptions.separateDialCode,
            initialValue,
          ])}
          phoneFieldRef={phoneFieldRef}
          initOptions={initOptions}
          field={field}
          initialValue={initialValue}
          errorCode={errorCode}
          handleChange={handleChange}
          setIsValid={setIsValid}
          setErrorCode={setErrorCode}
          setIsTouched={setIsTouched}
          onLoad={() => setIsPhoneInputLoaded(true)}
        />
      </Suspense>

      {!isSingleCountry &&
        isTouched &&
        !isValid &&
        errorCode !== null &&
        errorMap[errorCode] !== undefined && (
          <ErrorMessage variant="error" className="cp_error">
            {errorMap[errorCode]}
          </ErrorMessage>
        )}
    </div>
  );
}
